/**
 * Pure helpers for the `Responsibility` model.
 *
 * `Responsibility` carries two linked-entity fields that must coexist
 * during a multi-step rollout:
 *   - `linkedEntityId?: string`  — legacy single-link (older docs)
 *   - `linkedEntityIds?: string[]` — new multi-link list
 *
 * Read paths must always fold both fields together so we render correctly
 * regardless of which writer (iOS / Android / web / Cloud Functions) last
 * touched the document. Mirrors iOS
 * `Views/Responsibilities/ResponsibilitiesView.swift:349-366` which uses
 * `responsibility.allLinkedEntityIds` to drive the inline avatar+name row.
 *
 * The fold returns the deduped union of both fields, preserving the order
 * in which IDs first appear (plural list first, then the legacy singular
 * if it isn't already present). This mirrors the spec for Wave L.3 of the
 * web parity sweep ([[specs/web-ios-android-parity-sweep]]).
 */

import type { Responsibility } from "@/lib/models";

/**
 * Returns all linked entity IDs for a responsibility, folding both the
 * legacy `linkedEntityId` singular field and the new `linkedEntityIds`
 * plural field into a single deduped list.
 *
 * - Order: plural entries first (in their original order), then the
 *   legacy singular if not already present.
 * - Duplicates within the plural list are also collapsed.
 * - Returns `[]` when neither field is set or both are empty.
 */
export function allLinkedEntityIds(resp: Responsibility): string[] {
  const seen = new Set<string>();
  const out: string[] = [];

  for (const id of resp.linkedEntityIds ?? []) {
    if (!id) continue;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }

  if (resp.linkedEntityId && !seen.has(resp.linkedEntityId)) {
    seen.add(resp.linkedEntityId);
    out.push(resp.linkedEntityId);
  }

  return out;
}
